<template>
  <div>
    <zt-modal
      class="rounded font-normal"
      :active="active"
      :fullscreen="true"
      :show-button-close="false"
      :z-index="1001"
      @close="closeDialog"
      @shown="shownModal"
    >
      <template slot="modal-header">
        <div class="flex items-center justify-between py-4 px-8 relative">
          <h4>{{ $t('message.abm.folders.configurationDocumentModal') }}</h4>
          <button class="" @click="closeDialog" data-vs-button="closeDialog">
            <zt-icon name="times-solid" />
          </button>
        </div>
      </template>
      <template>
        <div class="w-full h-full relative">
          <div class="flex w-full h-full">
            <div
              class="h-full py-4 px-8 bg-zt-dark-200"
              style="min-width: 420px"
            >
              <signature-setting-panel ref="toolPanel" />
            </div>
            <div class="w-3/5 h-full bg-viewer" ref="containerPdf">
              <signature-pdf-visualizer ref="visorPDF" />
            </div>
            <div class="w-1/4 h-full bg-zt-dark-200"></div>
          </div>
          <div class="w-full h-full loading" v-if="isLoading">
            <vue-element-loading
              :active="isLoading"
              spinner="spinner"
              duration="1.5"
              color="#4f4f4f"
              size="50"
            />
          </div>
        </div>
      </template>
    </zt-modal>
    <confirm-sub-folder-config-dialog
      :active="isActiveConfirmConfigDialog"
      @handlerClick="beforeConfirmDialog"
    />
    <cancel-signature-config-dialog
      :active="isActiveConfirmLeaveDialog"
      @handlerClick="beforeConfirmDialog"
    />
    <error-dialog :message="messageError" :active="isActiveErrorDialog" />
  </div>
</template>
<script>
import { isEmpty, values } from 'lodash'
import VueElementLoading from 'vue-element-loading'
import CancelSignatureConfigDialog from '../../modals/CancelSignatureConfigDialog'
import ConfirmSubFolderConfigDialog from '../../modals/ConfirmSubFolderConfigDialog'
import ErrorDialog from '../../modals/ErrorDialog'
import SignaturePdfVisualizer from './SignaturePdfVisualizer'
import SignatureSettingPanel from './SignatureSettingPanel'
import {
  coordinateByRotation,
  transformCoordinates,
  rotateClockwise
} from '../../../utils'
import Signature from '@/apiClient/Signature'
export default {
  name: 'SignatureModalConfig',
  components: {
    CancelSignatureConfigDialog,
    ConfirmSubFolderConfigDialog,
    ErrorDialog,
    SignaturePdfVisualizer,
    SignatureSettingPanel,
    VueElementLoading
  },
  provide() {
    return {
      activeErrorDialog: this.activeErrorDialog,
      activeModalConfirm: this.activeModalConfirm,
      activeModalConfirmDiscard: this.activeModalConfirmDiscard,
      clearSignature: this.clearSignature,
      closeDialog: this.closeDialog,
      setFilePdf: this.setFilePdf,
      showFileDialog: this.showFileDialog,
      updateToolPanel: this.updateToolPanel,
      updateToolPanelApplied: this.updateToolPanelApplied,
      updateVisorPDF: this.updateVisorPDF,
      wrapperModal: this
    }
  },
  inject: [
    'activeModal',
    'modalState',
    'resetModalState',
    'stateFolder',
    'setModalState',
    'resetPreviewConfiguration',
    'setDocumentConfiguration',
    'setPreviewConfiguration'
  ],
  props: {
    active: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      cache: null,
      isActiveConfirmConfigDialog: false,
      isActiveConfirmLeaveDialog: false,
      isActiveErrorDialog: false,
      isLoading: true,
      messageError: ''
    }
  },
  created() {
    this.signatureApi = new Signature()
  },
  methods: {
    async setFilePdf() {
      try {
        const coords = this.modalState.config.cutPdf.preset.coordinates
        const points = this.modalState.config.cutPdf.preset.points
        const rotation = this.modalState.config.rotation % 360
        const toggleVal =
          rotation > 0 && !this.modalState.config.rotationPending
        const params = {
          crop: toggleVal ? points : values(coords),
          rotation: toggleVal ? 0 : rotation,
          file: this.modalState.config.file
        }
        const { data } = await this.signatureApi.setCustomPresetPdf(params)
        const base64 = Buffer(data, 'binary').toString('base64')
        const newConfig = {
          PDFJSObject: null,
          base64: `data:application/pdf;base64,${base64}`,
          cutPdf: {
            isFocused: true,
            isVisible: false,
            isChanged: true,
            preset: {
              applied: true,
              coordinates: this.modalState.config.cutPdf.preset.coordinates,
              points: this.modalState.config.cutPdf.preset.points,
              show: false
            }
          },
          file: new File([data], this.modalState.config.file.name, {
            type: 'application/pdf'
          }),
          orientation: this.modalState.config.orientation,
          page: 1,
          paper: this.modalState.config.paper,
          rotationPending: false,
          rotationApplied: rotation > 0
        }
        await this.$refs.toolPanel.signatureToolApplied(true, 'cutPdf')
        await this.updateVisorPDF(newConfig)
      } catch (e) {
        const code = e.response.data.codes[0].code
        this.messageError = this.$t(`message.abm.folders.errors.${code}`)
        this.activeErrorDialog(true)
      }
    },
    async setPendingRotateFilePdf() {
      try {
        const newCoords = {}
        const newPoints = []
        let changeCoords = false
        if (!isEmpty(this.modalState.config.cutPdf.preset.coordinates) && this.modalState.config.cutPdf.preset.applied) {
          const newValues = rotateClockwise(
            this.modalState.config.rotation,
            values(this.modalState.config.cutPdf.preset.coordinates)
          )
          const { coords, points } = coordinateByRotation(
            this.modalState.config.rotation,
            ...newValues
          )
          Object.assign(newCoords, coords)
          const [p1, p2, p3, p4] = points
          newPoints.push(p1, p2, p3, p4)
          changeCoords = true
        }
        const params = {
          crop: [0, 0, 0, 0],
          file: this.modalState.config.file,
          rotation: this.modalState.config.rotation
        }
        const { data } = await this.signatureApi.setCustomPresetPdf(params)
        const base64 = Buffer(data, 'binary').toString('base64')
        const newConfig = {
          PDFJSObject: null,
          base64: `data:application/pdf;base64,${base64}`,
          cutPdf: {
            isFocused: false,
            isVisible: false,
            isChanged: changeCoords,
            preset: {
              applied: true,
              coordinates: newCoords,
              points: values(newCoords),
              show: false
            }
          },
          file: new File([data], this.modalState.config.file.name, {
            type: 'application/pdf'
          }),
          orientation: this.modalState.config.orientation,
          page: 1,
          paper: this.modalState.config.paper,
          rotationPending: false,
          rotationApplied: this.modalState.config.rotation > 0
        }
        await this.setModalState(newConfig)
      } catch (e) {}
    },
    async beforeConfirmDialog() {
      const thumbs = await this.$refs.visorPDF.buildThumbnailsPreview()
      await this.resetPreviewConfiguration()
      await this.setPreviewConfiguration(thumbs)
      if (this.modalState.config.rotationPending)
        await this.setPendingRotateFilePdf()
      const {
        appliedConfig,
        base64,
        cutPdf,
        employee,
        employer,
        file,
        orientation,
        paper,
        rotation
      } = this.modalState.config
      const cropping = () => {
        if (isEmpty(cutPdf.preset.coordinates)) {
          return {
            crop: '',
            croppedApplied: false
          }
        }
        if (cutPdf.isChanged) {
          return {
            crop: cutPdf.preset.coordinates,
            croppedApplied: true
          }
        }
        return {
          crop: this.stateFolder.subFolderParams.documentConfiguration.crop,
          croppedApplied: this.stateFolder.subFolderParams.documentConfiguration.croppedApplied
        }
      }
      const documentConfiguration = {
        appliedConfig,
        ...(cropping()),
        rotation,
        orientation,
        paper,
        file,
        fileBase64: base64.split(',')[1],
        employeeSignature: transformCoordinates(employee.preset, true),
        employerSignature: transformCoordinates(employer.preset, true),
        rotationApplied: rotation > 0
      }
      await this.setDocumentConfiguration(documentConfiguration)
      this.activeModalConfirm(false)
      this.activeModal(false)
    },
    async updateVisorPDF(obj) {
      await this.setModalState(obj)
      this.$refs.visorPDF._updateInfo()
    },
    activeErrorDialog(active) {
      this.isActiveErrorDialog = active
    },
    activeModalConfirmDiscard(active) {
      this.isActiveConfirmLeaveDialog = active
    },
    activeModalConfirm(active) {
      this.isActiveConfirmConfigDialog = active
    },
    closeDialog() {
      if (!isEmpty(this.modalState.config.appliedConfig)) {
        this.activeModalConfirmDiscard(true)
        return
      }
      this.activeModal(false)
    },
    mappingDocumentConfig() {
      this.resetModalState()
      const newConfig = {}
      const {
        crop,
        employeeSignature,
        employerSignature,
        file,
        fileBase64,
        orientation,
        paper,
        rotation,
        rotationApplied
      } = this.stateFolder.subFolderParams.documentConfiguration
      const getPoints = crop => {
        const points = []
        if (crop) {
          const { left, top, right, bottom } = crop
          points.push(left, top, right, bottom)
        }
        return points
      }
      Object.assign(newConfig, {
        base64: `data:application/pdf;base64,${fileBase64}`,
        cutPdf: {
          isVisible: isEmpty(crop),
          isFocused: false,
          isChanged: false,
          preset: {
            applied: !isEmpty(crop),
            coordinates: !isEmpty(crop) ? crop : {},
            points: getPoints(crop),
            show: false
          }
        },
        employee: {
          isFocused: employeeSignature.show,
          isVisible: employeeSignature.show,
          preset: transformCoordinates(employeeSignature)
        },
        file,
        page: 1,
        orientation,
        paper,
        rotation: parseInt(rotation),
        rotationApplied,
        size: this.$refs.containerPdf.getBoundingClientRect()
      })
      if (!isEmpty(employerSignature)) {
        Object.assign(newConfig, {
          employer: {
            isFocused: false,
            isVisible: employerSignature.show,
            preset: transformCoordinates(employerSignature)
          }
        })
      }
      return newConfig
    },
    showFileDialog() {
      this.$refs.toolPanel.showFileDialog()
    },
    shownModal() {
      const mappedDocumentConfig = this.mappingDocumentConfig()
      this.setModalState(mappedDocumentConfig)
      this.$nextTick(() => {
        this.$refs.visorPDF._updateInfo()
        if (!mappedDocumentConfig.file) this.isLoading = false
      })
    },
    updateToolPanel(obj) {
      this.setModalState(obj)
      this.$refs.toolPanel._updateInfo()
    },
    updateToolPanelApplied(add, id) {
      this.$refs.toolPanel.signatureToolApplied(add, id)
    }
  }
}
</script>
<style scoped lang="scss">
.loading {
  position: absolute !important;
  top: 0;
  left: 0;
  z-index: 10;
  background: #fff;
}
::v-deep {
  .zt-modal {
    box-shadow: 0 3px 4px rgba(0, 0, 0, 0.14), 0 3px 3px rgba(0, 0, 0, 0.12),
      0 1px 8px rgba(0, 0, 0, 0.2);
  }
  .zt-modal.rounded {
    border-radius: 0.375rem !important;
  }
  button.button-cancel {
    color: #707070 !important;
    background-color: transparent !important;
  }
  .zt-modal .zt-modal-body {
    height: calc(100% - 50px);
  }
}
</style>